import { hideModal, showModal } from 'managers/modal'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent, useState } from 'react'
import { loginUser } from 'api/auth'
import { assignGuestChatsToCurrentUser, getMyChats } from 'api/chats'
import { InputPassword } from 'components/ui/InputPassword'
import { ResetPasswordModal } from 'components/modals/ResetPasswordModal'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { getUserNameSurname } from 'utils/user'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { RegisterModal } from 'components/modals/RegisterModal'

type LoginModalProps = {
  onSuccess?: () => void
  onResetPassword?: () => void
  onRegister?: () => void
}

export const LoginModal = ({ onSuccess, onResetPassword, onRegister }: LoginModalProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: createValidationSchema({
      email: required,
      password: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      console.debug({ values })
      try {
        setLoading(true)
        const user = await loginUser(values.email, values.password)
        await assignGuestChatsToCurrentUser()
        await getMyChats({})
        onSuccess?.()
        const nameSurname = getUserNameSurname(user)
        showSuccessToast(`${t('welcomeLogin')} ${nameSurname}`)
        hideModal()
      } catch (e) {
        console.error({ e })
        if (e === 'userNotAllowedToLogin') {
          showErrorToast(t('userNotAllowedToLogin'))
        } else {
          showErrorToast(t('loginError'))
        }
      } finally {
        setLoading(false)
      }
    },
  })

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const onClickForgottenPassword = () => {
    if (onResetPassword) {
      return onResetPassword()
    }
    hideModal()
    showModal({
      children: <ResetPasswordModal email={values.email} />,
    })
  }

  const onClickRegister = () => {
    if (onRegister) {
      return onRegister()
    }
    hideModal()
    showModal({
      children: <RegisterModal />,
    })
  }

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  return (
    <div style={{ padding: 20 }}>
      <div className={'tw-flex tw-flex-row tw-items-center tw-justify-between'}>
        <div className={'tw-font-[800]'}>{t('login')}</div>
        <div className={'tw-cursor-pointer'}>
          <FontAwesomeIcon icon={['far', 'times']} onClick={hideModal} />
        </div>
      </div>
      <div className={'tw-flex tw-flex-col tw-space-y-1 tw-mt-2'}>
        <Input label={t('email')} {...fieldProps('email')} onKeyDown={onKeyDown} />
        <InputPassword label={t('password')} {...fieldProps('password')} onKeyDown={onKeyDown} />
        <div className={'tw-h-2'} />
        <div className={'tw-text-sm tw-underline tw-cursor-pointer'} onClick={onClickForgottenPassword}>
          {t('forgottenPassword')}
        </div>
        <div className={'tw-text-sm tw-underline tw-cursor-pointer'} onClick={onClickRegister}>
          {t('dontHaveAccount')}
        </div>
      </div>
      <div className={'tw-mt-6 tw-w-full'}>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={loading}>
          <span className={'tw-text-sm'}>{t('login')}</span>
        </UIButton>
      </div>
    </div>
  )
}
